import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSettings } from "../settings/context";
import { SaveState } from "./SaveState";
import { TranslateAll } from "./TranslateAll";
import {
  FUNNEL,
  PICKER_GROUPS,
  screenOf,
  stripContext,
  withContext,
} from "../routes/registry";
import {
  ORDER_KEY,
  instanceKey,
  instancePath,
  orderedEntries,
} from "../funnel/order";
import { fallbackOf } from "../settings/catalog";
import { useSitePages } from "../pages/nav";
import { stepPageOf } from "../pages/stepPages";
import { FIXED_PAGES, pagePath } from "../pages/registry";
import { scopeKindOf } from "../settings/scope";
import { languageName } from "../settings/nomDeLangue";

/**
 * THE ORGANISER BAR — the tool's own top bar, above the site.
 *
 * It answers "WHAT AM I LOOKING AT": which event, which screen, as which
 * typology, on which device. The panel on the right answers "what am I
 * changing". Keeping the two apart is what stops the tool from feeling like a
 * back office bolted on top of the site.
 *
 * Three deliberate placements:
 *
 *   — The SCREEN picker lives here, not in the site's navigation, because it
 *     must also reach screens removed FROM that navigation (see registry.ts).
 *
 *   — The TYPOLOGY lives here because it is an organiser's viewpoint, not a
 *     product control: a participant only ever has one. The LANGUAGE sits right
 *     of it AND stays in the site: a visitor genuinely picks it down there, but
 *     an organiser has no reflex to reach into the page for the axis that decides
 *     where their translation is written.
 *
 *   — AN OVERRIDE TINTS THE BAR. Writing for one language or one typology is
 *     announced by the bar itself and by the selector that caused it, in a hue of
 *     its own (`--o-over`), not the editor's usual cyan.
 *
 *   — THE RECAP sits with the reading controls: it is something you read, like
 *     the screen and typology pickers.
 *   — LEAVE sits at the far end, away from them all.
 *
 * There is NO "reset" here any more. It wiped the whole configuration without
 * asking, three centimetres from the button you press to check your work — too
 * dangerous to keep within reach of a live event.
 *
 * Colours are the neutral `--o-*` set, never the client's brand. An organiser
 * whose event is navy blue must still be able to tell the editor from their site.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */
export function OrganiserBar({
  view,
  onView,
  asAnonymous,
  onAsAnonymous,
  onRecap,
  onLeave,
}: {
  view: "desktop" | "mobile";
  onView: (v: "desktop" | "mobile") => void;
  /** Regarder le site comme quelqu'un qui n'a pas de compte. Voir `panel/mode.ts`. */
  asAnonymous: boolean;
  onAsAnonymous: (v: boolean) => void;
  onRecap: () => void;
  onLeave: () => void;
}) {
  const { scope, languages, typos, setLanguage, setTypo, value } =
    useSettings();
  /* Où atterrit la prochaine écriture : la même règle que le bandeau de surcharge et le
     panneau (`settings/scope.ts`). La barre s'en teinte, voir `chrome.css`. */
  const scopeKind = scopeKindOf(scope);
  const navigate = useNavigate();
  const { pathname: rawPath } = useLocation();
  const { context } = useParams();
  const pathname = stripContext(rawPath, context);
  /* ⚠️ UNE ÉTAPE SERVIE EN PAGE EST UN ÉCRAN CONNU. `screenOf` ne connaît que le
     registre et les pages du site : sans ce second terme, la barre annonçait « Hors
     écran » sur une adresse qu'elle proposait elle-même deux lignes plus bas — et
     l'entrée factice, posée en tête avec la même valeur, gagnait la sélection. */
  const known =
    screenOf(pathname) !== undefined || stepPageOf(pathname) !== undefined;
  const { name: pageName, steps: stepsAsPages, ordered } = useSitePages();
  /* Les entrées fixes ont leur propre destination (la racine, la première étape servie) :
     elles ne se choisissent pas ici, elles s'atteignent par le site. */
  const pagesRangees = ordered.filter(
    (p) => !p.nameKey && !FIXED_PAGES.some((f) => f.key === p.key),
  );

  /**
   * ═══ LES ÉTAPES, TELLES QUE L'ORGANISATEUR LES A RANGÉES ═══
   *
   * ⚠️ PAS LA LISTE DU REGISTRE. Elle ne connaît qu'UNE étape « informations » : la
   * seconde n'apparaissait nulle part dans ce sélecteur, et il n'existait aucun chemin
   * dans l'outil pour atteindre ses réglages — donc pas moyen de lui choisir son
   * formulaire. Mesuré : une seule entrée `/registration/informations`.
   *
   * ⚠️ TOUTES LES ÉTAPES, Y COMPRIS CELLES QUI NE SONT PAS SERVIES — même raison que les
   * pages dépubliées juste en dessous : sans elles, éteindre une étape rendait ses
   * réglages inatteignables, et il fallait la rallumer devant les participants pour y
   * revenir. `orderedEntries` complète, donc la liste les porte toutes.
   */
  const steps = orderedEntries(value(ORDER_KEY)).flatMap((entry) => {
    const step = FUNNEL.find((s) => s.key === entry.key);
    if (!step?.funnel) return [];
    const written = value(instanceKey(step.funnel, entry.instance));
    return [
      {
        path: instancePath(step.path, entry.instance),
        /* Le mot de l'événement, et le repli sur la clé de BASE : celui d'une instance
           engendrée n'existe pas dans l'index du catalogue. */
        label:
          typeof written === "string" && written.trim()
            ? written
            : fallbackOf(step.funnel),
      },
    ];
  });

  return (
    <div className="obar" data-scope={scopeKind}>
      {/**
       * ⚠️ RIEN À GAUCHE DU SÉLECTEUR D'ÉCRAN, QUE LA PASTILLE. Il y avait le mot
       * « Organisation » et le nom de l'événement, deux réductions successives (le mot
       * sous 1520 px, le nom sous 1120 px) pour une place que la barre n'a jamais eue.
       * Aucun des deux n'apprenait quoi que ce soit : le nom de l'événement est écrit
       * DANS le site, à trois centimètres de là, et l'organisateur sait chez qui il est
       * puisqu'il vient d'y entrer. Retirés à la demande de l'user, la place va aux
       * commandes.
       *
       * La pastille reste : c'est elle qui dit « vous êtes en édition », et elle coûte
       * huit pixels.
       */}
      <span className="obar__id">
        <span className="obar__dot" />
      </span>

      <label className="obar__go">
        <span className="obar__golab">Écran</span>
        <select
          value={pathname}
          aria-label="Écran à configurer"
          onChange={(e) => navigate(withContext(e.target.value, context))}
        >
          {/* An unregistered address gets its OWN entry. Without it the browser
              falls back to the first option, and the bar claims you are on the
              home screen while you are looking at something else — a control that
              states a false position is worse than one that states nothing. */}
          {!known && (
            <option value={pathname} disabled>
              Hors écran
            </option>
          )}
          {/* ⚠️ UNE SEULE BOUCLE, SUR UNE LISTE QUE LE TEST VOIT. Les écrans fixes
              étaient nommés ici, en JSX : le test d'orphelins recopiait la même
              composition et ne pouvait donc rien attraper. Ils sont maintenant dans
              `PICKER_GROUPS`, où l'on peut vérifier qu'aucun écran n'y manque. Les
              outils du direct sont un groupe à part parce qu'ils ne sont ni des
              pages du site ni des étapes du parcours : un participant ne les voit
              jamais. */}
          {PICKER_GROUPS.map((group) => {
            const options = group.screens.map((s) => (
              <option key={s.path} value={s.path}>
                {s.label}
              </option>
            ));
            return group.label ? (
              <optgroup key={group.label} label={group.label}>
                {options}
              </optgroup>
            ) : (
              options
            );
          })}
          {/* Numérotées comme dans « Étapes du parcours », et pour une raison précise :
              deux étapes « informations » que rien ne distingue sont impossibles à viser.
              Le rang est ce qui fait correspondre cette liste et le gestionnaire. */}
          <optgroup label="Étapes de l'inscription">
            {steps.map((e, i) => (
              <option key={e.path} value={e.path}>
                {i + 1}. {e.label}
              </option>
            ))}
          </optgroup>
          {/**
           * ⚠️ THE SITE'S PAGES, INCLUDING THE UNPUBLISHED ONES — that is this picker's
           * whole purpose, written in the registry with "Mon badge" as its example.
           * Without them, taking a page out of the navigation made it impossible to
           * configure: it had to be republished, in front of the participants, to reach
           * its settings.
           *
           * ⚠️ UNDER THE NAME THE ORGANISER GAVE THEM. Renaming "Mon badge" to "Mon
           * accès" and still reading "Badge" here would make anyone doubt they are in
           * the right place — the prototype reaches the same conclusion ("renaming a
           * page follows everywhere").
           */}
          {/* ⚠️ DANS L'ORDRE DU SITE, celui du gestionnaire — pas celui du registre. Une
              liste qui ne se lit pas dans le même ordre que le menu qu'on vient de
              ranger oblige à chercher deux fois. */}
          <optgroup label="Pages du site">
            {pagesRangees.map((p) => (
              <option key={p.key} value={pagePath(p)}>
                {pageName(p)}
              </option>
            ))}
            {/* ⚠️ LA MÊME ÉTAPE APPARAÎT DEUX FOIS, ET C'EST EXACT : une fois comme
                étape du tunnel, une fois comme page du site. Ce sont deux ADRESSES du
                même écran, et il ne se présente pas pareil aux deux — sans cette
                entrée, l'organisateur ne pourrait jamais voir la page qu'il vient
                d'activer autrement qu'en se déconnectant pour passer par le menu. */}
            {stepsAsPages.map((p) => (
              <option key={`step:${p.key}`} value={pagePath(p)}>
                {pageName(p)} (page)
              </option>
            ))}
          </optgroup>
        </select>
      </label>

      <span className="obar__sep" />

      {/* ⚠️ CHAQUE SÉLECTEUR QUI SURCHARGE SE SIGNALE LUI-MÊME. Le bandeau dit « vous
          êtes en surcharge » ; ceci dit LEQUEL des deux axes l'a provoquée, et donc
          lequel remettre pour revenir à la référence. */}
      <label
        className="obar__go"
        data-over={scope.typoId !== null && scope.typoId !== undefined}
      >
        <span className="obar__golab">En tant que</span>
        <select
          value={scope.typoId ?? ""}
          aria-label="Typologie de participant"
          onChange={(e) =>
            setTypo(e.target.value === "" ? null : Number(e.target.value))
          }
        >
          {typos.map((t) => (
            <option key={t.id ?? "none"} value={t.id ?? ""}>
              {t.name}
            </option>
          ))}
        </select>
      </label>

      {/**
       * ═══ LA LANGUE, À DROITE DE « EN TANT QUE » ═══
       *
       * ⚠️ REVENUE DANS LA BARRE, à la demande de l'user (#227). Elle en avait été retirée
       * le 2026-08-20 parce que le site en porte déjà une ; mais c'est l'un des deux axes
       * de la surcharge, et l'organisateur va le chercher là où est l'autre, pas dans la
       * page. Celui du site RESTE : il fait partie de ce qu'on prévisualise.
       *
       * ⚠️ `setLanguage` ET JAMAIS `chooseLanguage`. Lire dans une langue pour la rédiger
       * n'est pas la choisir : sans cette distinction, un organisateur qui traduit son site
       * en six langues repartirait avec le chinois sur son compte. Les deux sélecteurs
       * pilotent le même état, ils ne peuvent pas se contredire.
       */}
      <label
        className="obar__go"
        data-over={scope.languageId !== scope.defaultLanguageId}
      >
        <span className="obar__golab">Langue</span>
        <select
          value={scope.languageId}
          aria-label="Langue de rédaction"
          onChange={(e) => setLanguage(Number(e.target.value))}
        >
          {/* Les mêmes noms que le sélecteur du site : deux listes qui pilotent le
              même état ne nomment pas la même langue de deux façons. */}
          {languages.map((l) => (
            <option key={l.id} value={l.id}>
              {languageName(l.short, l.name)}
            </option>
          ))}
        </select>
      </label>

      <span className="obar__sp" />

      <SaveState />

      <TranslateAll />

      {/**
       * ═══ POUR QUI ON REGARDE : quelqu'un de connecté, ou quelqu'un qui passe ═══
       *
       * ⚠️ AU MÊME ENDROIT QUE LE FORMAT D'APERÇU, et pour la même raison : ce sont
       * les deux commandes qui changent CE QU'ON VOIT sans rien écrire. « En tant
       * que » (langue, typologie), à gauche, choisit la configuration servie ; ces
       * deux-là choisissent l'œil qui la lit.
       *
       * ⚠️ CE N'EST PAS UNE DÉCONNEXION, et le survol le dit en toutes lettres : les
       * requêtes partent toujours avec le jeton. Fidèle pour le menu, les tuiles et
       * la garde ; muet sur ce que le serveur filtre. La seule vraie mesure reste une
       * fenêtre privée, et mieux vaut l'écrire ici que de le laisser découvrir.
       */}
      {/* Le mot au-dessus des deux, comme « Écran », « Langue » et « En tant que » :
          sans lui, « Connecté / Non connecté » posé à côté d'une typologie se lit comme
          un troisième filtre de configuration, ce qu'il n'est pas. */}
      <span className="obar__golab obar__vu">Vu par</span>
      <div className="oseg" role="group" aria-label="Vu par">
        <button
          type="button"
          title="Voir le site comme une personne connectée"
          aria-pressed={!asAnonymous}
          onClick={() => onAsAnonymous(false)}
        >
          {/* ⚠️ UN DESSIN, ET PAS PAR COQUETTERIE : sous 1648 px la barre passe ses
              segments à `font-size: 0`, et un bouton de TEXTE SEUL y devient une
              pastille sombre et vide. C'est écrit dans la feuille, à cette règle. */}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21a8 8 0 0 1 16 0" />
          </svg>
          Connecté
        </button>
        <button
          type="button"
          title="Voir le site comme quelqu'un qui n'a pas de compte. Aperçu seulement : les requêtes gardent votre session."
          aria-pressed={asAnonymous}
          onClick={() => onAsAnonymous(true)}
        >
          {/* Le globe : tout le monde, personne en particulier. Le même que le site
              emploie pour la langue servie — c'est le dessin du « public » ici. */}
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" />
          </svg>
          Non connecté
        </button>
      </div>

      {/* Preview format. It genuinely narrows `.site`, which carries
          `container-type: inline-size` — so every @container rule reacts exactly
          as it would on a real phone. Nothing is simulated. */}
      <div className="oseg" role="group" aria-label="Format d'aperçu">
        <button
          type="button"
          title="Aperçu bureau"
          aria-pressed={view === "desktop"}
          onClick={() => onView("desktop")}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="2" y="4" width="20" height="13" rx="2" />
            <path d="M8 21h8" />
          </svg>
          Bureau
        </button>
        <button
          type="button"
          title="Aperçu mobile"
          aria-pressed={view === "mobile"}
          onClick={() => onView("mobile")}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="7" y="2" width="10" height="20" rx="2" />
            <path d="M11 18h2" />
          </svg>
          Mobile
        </button>
      </div>

      {/* The recap sits next to the reading controls, not with the exit: it is
          something you READ, like the screen and typology pickers. */}
      <button
        type="button"
        className="obtn obtn--peek"
        title="Voir ce qui est réglé et ce qui diffère entre typologies"
        onClick={onRecap}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
        Récapitulatif
      </button>

      <button type="button" className="obtn obtn--go" onClick={onLeave}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        Voir en visiteur
      </button>
    </div>
  );
}
